import { Module } from "vuex";
import { State } from "../store";
import { httpsCallable } from "firebase/functions";
import { auth, functions, getAppCheckToken } from "@/plugins/firebase";
import { BestRecords } from "@/common/types/BestRecords";
import { MusicQuery } from "@/common/types/MusicQuery";
import { BestRecordsCriteria } from "@/common/types/BestRecordsCriteria";
import { BestRecordData } from "@/common/types/BestRecordData";
import { deserializeDate, SerializedDate } from "@/common/types/SerializedDate";
import Music from "@/models/Music";
import Chart from "@/models/Chart";
import BestRecord from "@/models/BestRecord";
import { CLEAR_TYPE } from "@/common/types/ClearType";
import { ExtendMap } from "@/models/ExtendMap";
import isEqual from "lodash/isEqual";
import MusicRowData from "@/models/MusicRowData";
import { RecordData } from "@/common/types/RecordData";
import { ChartRecordsCriteria } from "@/common/types/ChartRecordsCriteria";
import Record from "@/models/Record";
import { compareSort } from "@/utils/compareSort";
import { compareFilter } from "@/utils/compareFilter";
import { textSearch } from "@/utils/textSearch";
import { Memo } from "@/common/types/Memo";
import axios from "@/plugins/axios";
import { LATEST_VERSION } from "@/common/types/Version";
import { DIFFICULTY_CRITERIA } from "@/common/types/Difficulty";

export type RecordState = {
  masterData: {
    music: Music[] | null;
    chart: Chart[] | null;
  };
  bestRecords: { criteria: BestRecordsCriteria; record: BestRecord[] }[];
  chartRecords: { criteria: ChartRecordsCriteria; record: Record[] }[];
};

const recordModule: Module<RecordState, State> = {
  namespaced: true,
  state: {
    masterData: { music: null, chart: null },
    bestRecords: [],
    chartRecords: [],
  },
  mutations: {
    setMasterData(state, masterData) {
      state.masterData = masterData;
    },
    addBestRecords(state, bestRecords) {
      state.bestRecords.push(bestRecords);
    },
    addChartRecords(state, chartRecords) {
      state.chartRecords.push(chartRecords);
    },
    removeRecords(state) {
      state.bestRecords = [];
      state.chartRecords = [];
    },
  },
  actions: {
    async getMasterData({ state, commit }) {
      if (state.masterData.music !== null && state.masterData.chart !== null) {
        return state.masterData;
      }

      const response = await axios({ url: "/getMasterData" });

      const result = response.data;

      const musicArray: Music[] = [];
      const musicMap = new ExtendMap<number, ExtendMap<number, Music>>();

      for (const musicData of result.data.music) {
        const revisionMap = musicMap.getOrDefault(musicData.id, new ExtendMap<number, Music>());
        musicMap.set(musicData.id, revisionMap);

        const music = new Music(musicData, revisionMap);

        musicArray.push(music);
        revisionMap.set(musicData.revision, music);
      }

      const chartArray: Chart[] = [];

      for (const chartData of result.data.chart) {
        const music = musicMap.getOrDefault(chartData.musicId, new ExtendMap()).get(chartData.revision);

        if (music === undefined) {
          console.log(`not found. ${chartData.musicId} ${chartData.revision}`);
          continue;
        }

        const chart = new Chart(music, chartData);

        chartArray.push(chart);
      }

      const masterData = { music: musicArray, chart: chartArray };
      commit("setMasterData", masterData);
      return masterData;
    },
    async getBestRecords({ state, commit, dispatch }, data: BestRecordsCriteria) {
      if (data.beginVersion !== null && data.beginVersion <= 0) {
        data.beginVersion = (LATEST_VERSION as number) + data.beginVersion;
      }

      if (data.endVersion !== null && data.endVersion <= 0) {
        data.endVersion = (LATEST_VERSION as number) + data.endVersion;
      }

      let find = state.bestRecords.find((e) => isEqual(e.criteria, data));

      if (find === undefined) {
        const token = await auth.currentUser?.getIdToken(true);
        const appCheckToken = await getAppCheckToken();

        const getRecords = axios({
          url: "/getRecords-bestGet",
          params: data,
          headers: {
            Authorization: token !== null ? `Bearer ${token}` : undefined,
            "x-firebase-appcheck": appCheckToken ?? "debugToken",
          },
        }).then((e) => deserializeDate(e.data) as BestRecords);

        const [masterData, records]: [
          {
            music: NonNullable<typeof state.masterData.music>;
            chart: NonNullable<typeof state.masterData.chart>;
          },
          BestRecords
        ] = await Promise.all([dispatch("getMasterData"), getRecords]);

        const recordMap = new ExtendMap<number, ExtendMap<number, ExtendMap<number, BestRecordData>>>();

        for (const recordData of records.record) {
          const musicMap = recordMap.getOrDefault(
            recordData.musicId,
            new ExtendMap<number, ExtendMap<number, BestRecordData>>()
          );
          musicMap.set(
            recordData.revision,
            musicMap
              .getOrDefault(recordData.revision, new ExtendMap<number, BestRecordData>())
              .set(recordData.difficulty, recordData)
          );
          recordMap.set(recordData.musicId, musicMap);
        }

        find = { record: [], criteria: data };

        for (const chart of masterData.chart) {
          if (chart.playStyle !== records.criteria.playStyle) {
            continue;
          }

          let recordData = recordMap
            .getOrDefault(chart.music.id, new ExtendMap<number, ExtendMap<number, BestRecordData>>())
            .getOrDefault(chart.music.revision, new ExtendMap<number, BestRecordData>())
            .get(chart.difficulty);

          if (recordData === undefined) {
            recordData = {
              musicId: chart.music.id,
              revision: chart.music.revision,
              playStyle: chart.playStyle,
              difficulty: chart.difficulty,
              score: 0,
              pGreat: 0,
              great: 0,
              missCount: 65535,
              clearType: CLEAR_TYPE.NO_PLAY,
              date: null,
              playVersion: null,
              playCount: 0,
              scoreDate: null,
              scoreVersion: null,
              missCountDate: null,
              missCountVersion: null,
              clearTypeDate: null,
              clearTypeVersion: null,
            };
          }
          find.record.push(new BestRecord(chart, recordData));
        }

        commit("addBestRecords", find);
      }

      return find.record;
    },
    async getMusicRowData({ dispatch }, data: MusicQuery) {
      const [memoArray, bestRecords1, bestRecords2]: [Memo[], BestRecord[], BestRecord[] | null] = await Promise.all([
        dispatch("memo/getMemoArray", null, { root: true }),
        dispatch("getBestRecords", data.bestRecordsCriteria1),
        data.bestRecordsCriteria2 !== null
          ? dispatch("getBestRecords", data.bestRecordsCriteria2)
          : Promise.resolve(null),
      ]);

      const memo2Map = new ExtendMap<number, ExtendMap<number, ExtendMap<number, Memo>>>();
      for (const memo of memoArray) {
        if (memo.playStyle !== data.bestRecordsCriteria1.playStyle) {
          continue;
        }
        memo2Map.set(
          memo.musicId,
          memo2Map
            .getOrDefault(memo.musicId, new ExtendMap<number, ExtendMap<number, Memo>>())
            .set(
              memo.revision,
              memo2Map
                .getOrDefault(memo.musicId, new ExtendMap<number, ExtendMap<number, Memo>>())
                .getOrDefault(memo.revision, new ExtendMap<number, Memo>())
                .set(memo.difficulty, memo)
            )
        );
      }

      const bestRecord2Map = new ExtendMap<number, ExtendMap<number, ExtendMap<number, BestRecord>>>();
      if (bestRecords2 !== null) {
        for (const bestRecord of bestRecords2) {
          bestRecord2Map.set(
            bestRecord.chart.music.id,
            bestRecord2Map
              .getOrDefault(bestRecord.chart.music.id, new ExtendMap<number, ExtendMap<number, BestRecord>>())
              .set(
                bestRecord.chart.music.revision,
                bestRecord2Map
                  .getOrDefault(bestRecord.chart.music.id, new ExtendMap<number, ExtendMap<number, BestRecord>>())
                  .getOrDefault(bestRecord.chart.music.revision, new ExtendMap<number, BestRecord>())
                  .set(bestRecord.chart.difficulty, bestRecord)
              )
          );
        }
      }

      const result = [];
      for (const bestRecord of bestRecords1) {
        const musicRowData = new MusicRowData(
          bestRecord,
          data.bestRecordsCriteria1,
          memo2Map
            .getOrDefault(bestRecord.chart.music.id, new ExtendMap<number, ExtendMap<number, Memo>>())
            .getOrDefault(bestRecord.chart.music.revision, new ExtendMap<number, Memo>())
            .get(bestRecord.chart.difficulty) ?? null,
          bestRecord2Map
            .getOrDefault(bestRecord.chart.music.id, new ExtendMap<number, ExtendMap<number, BestRecord>>())
            .getOrDefault(bestRecord.chart.music.revision, new ExtendMap<number, BestRecord>())
            .get(bestRecord.chart.difficulty),
          data.bestRecordsCriteria2 !== null ? data.bestRecordsCriteria2 : undefined
        );
        result.push(musicRowData);
      }

      return result
        .filter((e) => (data.version === null ? true : data.version.includes(e.bestRecord1.chart.music.version)))
        .filter((e) =>
          data.level === null
            ? true
            : e.bestRecord1.chart.level !== null && data.level.includes(e.bestRecord1.chart.level)
        )
        .filter((e) => (data.clearType === null ? true : data.clearType.includes(e.bestRecord1.clearType)))
        .filter((e) =>
          data.djLevel === null
            ? true
            : e.bestRecord1.exDjLevel !== null && data.djLevel.includes(e.bestRecord1.exDjLevel)
        )
        .filter((e) => {
          if (data.difficulty === null) return true;
          if (data.difficulty.includes(e.bestRecord1.chart.difficulty)) return true;
          return data.difficulty.includes(DIFFICULTY_CRITERIA.MOST_DIFFICULT) && e.bestRecord1.chart.isMostDifficult;
        })
        .filter((e) => {
          if (data.radar === null) return true;
          if (e.bestRecord1.chart.maxRadarElements === null) return false;
          return e.bestRecord1.chart.maxRadarElements.some((f) => (data.radar ?? []).includes(f));
        })
        .filter((e) => (data.compare === null ? true : compareFilter(e.bestRecord1, e.bestRecord2, data.compare)))
        .filter((e) => textSearch(e, data.text ?? []))
        .filter((e) => (data.bpm.soflan === null ? true : e.bestRecord1.chart.isSoflan === data.bpm.soflan))
        .filter((e) => (data.bpm.minBpm === null ? true : e.bestRecord1.chart.minBpm >= data.bpm.minBpm))
        .filter((e) => (data.bpm.maxBpm === null ? true : e.bestRecord1.chart.maxBpm <= data.bpm.maxBpm))
        .filter((e) => (data.other.delete === null ? true : e.bestRecord1.chart.music.isDeleted === data.other.delete))
        .sort((a, b) => compareSort(a, b, data.sort));
    },
    async getChartRecords({ state, commit, dispatch }, data: ChartRecordsCriteria) {
      let find = state.chartRecords.find((e) => isEqual(e.criteria, data));

      if (find === undefined) {
        const fn = httpsCallable(functions, "getRecords-chart");

        const [masterData, records]: [
          {
            music: NonNullable<typeof state.masterData.music>;
            chart: NonNullable<typeof state.masterData.chart>;
          },
          { record: RecordData[] }
        ] = await Promise.all([
          dispatch("getMasterData"),
          deserializeDate((await fn(data)).data as SerializedDate<{ record: RecordData[] }>),
        ]);

        find = { record: [], criteria: data };

        const chart = masterData.chart.find(
          (e) =>
            e.music.id === data.musicId &&
            e.music.revision === data.revision &&
            e.playStyle === data.playStyle &&
            e.difficulty === data.difficulty
        );

        if (chart === undefined) {
          return find;
        }

        for (const record of records.record) {
          find.record.push(new Record(chart, record));
        }

        commit("addChartRecords", find);
      }

      return find.record;
    },
    removeRecords({ commit }) {
      commit("removeRecords");
    },
  },
};

export default recordModule;
